import React, { useEffect, useCallback } from "react";
import { usePrevious } from "../../hooks";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import { Delete as DeleteIcon, Send as SendIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Field } from "react-final-form";

import { TextField } from "../common/Fields";
import {
  getApplication,
  submitApplication,
  updateApplication,
  deleteApplication,
  fetchOrgs,
} from "../../actions";
import { Content } from "../layout";

const ApplicationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { application } = useSelector((state) => state.application);
  const { user } = useSelector((state) => state.auth);
  const prevOrganization = usePrevious(user?.Organization?.id);

  useEffect(() => {
    dispatch(fetchOrgs({}));
    if (id) {
      dispatch(getApplication(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (user && prevOrganization) {
      if (user.Organization.id !== prevOrganization) {
        navigate("/applications");
      }
    }
  }, [user, prevOrganization, navigate]);

  const onSubmit = useCallback(
    async (application) => {
      const upsertApplication = new Promise((resolve, reject) => {
        if (id) {
          dispatch(updateApplication({ application, id }))
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          dispatch(submitApplication({ application }))
            .then(() => resolve())
            .catch((error) => reject(error));
        }
      });

      try {
        await upsertApplication;
        navigate("/applications");
      } catch (error) {
        let errors;
        if (error.response.data?.errors) {
          const arrayErrors = error.response.data.errors;
          arrayErrors.forEach((error) => {
            errors = { ...errors, [error.param]: error.msg };
          });
        }
        return errors;
      }
    },
    [id, dispatch, navigate]
  );

  const onDelete = useCallback(
    (e) => {
      e.preventDefault();
      if (window.confirm("삭제하시겠습니까?")) {
        dispatch(deleteApplication({ id }))
          .then(() => navigate("/applications"))
          .catch((error) => alert(error));
      }
    },
    [id, dispatch, navigate]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        OrganizationId: application?.Organization?.id ?? user?.Organization?.id,
        name: application?.name,
        description: application?.description,
      }}
      validate={(values) => {
        const { name, description } = values;
        // console.log("values", values);
        const errors = {};
        if (!name) {
          errors.name = "필수";
        }
        if (!description) {
          errors.description = "필수";
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <Content>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  협력사
                </Typography>
                <Grid
                  container
                  alignItems="flex-start"
                  spacing="20"
                  sx={{ mt: 2 }}
                >
                  <Grid item xs={12}>
                    {user && (
                      <Box>
                        <InputLabel shrink={true}>건설사</InputLabel>
                        <Box>
                          {application?.Organization?.displayName ??
                            user?.Organization.displayName}
                        </Box>
                        <Field
                          name="OrganizationId"
                          type="hidden"
                          render={({ input, meta }) => <input {...input} />}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="name"
                      type="text"
                      label="구분값"
                      helperText="영문자, 숫자, '-'만 가능합니다."
                      inputProps={{
                        pattern: "[\\w-]+",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="description"
                      type="text"
                      label="협력사 이름"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {submitError && <FormHelperText>{submitError}</FormHelperText>}
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={6}>
                {id && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                  >
                    삭제
                  </Button>
                )}
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  저장
                </Button>
              </Grid>
            </Grid>
          </form>
        </Content>
      )}
    />
  );
};

export default ApplicationForm;
